import { Form } from 'antd';
import Box from 'components/Box';
import Modal from 'components/Modal';
import appointmentApis from 'features/appointment/services/apis';
import { aptUIActions } from 'features/appointment/services/reducers';
import appointmentSelectors from 'features/appointment/services/selectors';
import {
  IBodyApiBookingMultiple,
  IBodyApiBookingMultipleItem,
} from 'features/appointment/services/types/api';
import { IAppointmentItemData } from 'features/appointment/services/types/appointment';
import { DATE_FORMAT } from 'features/appointment/utils/format';
import { clone, delay, first, get, padEnd } from 'lodash';
import moment, { Moment } from 'moment';
import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { IResponseDataBody } from 'services/response';
import shopSelectors from 'services/shop/selectors';
import { IStaffItemData } from 'services/shop/types/staff';
import { useSetLoadingPage } from 'services/UI/LoadingPage';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import toast from 'utils/toast';
import { verifyPinCode } from 'widgets/RequestAdminLocalPW/Callback';
import QuickBookingCustomerContent from './CustomerContent';
import QuickBookingServiceContent from './ServiceContent';
import { useStaffListChooseUI } from './Staff';
import QuickBookingStaffContent from './StaffContent';
import QuickBookingTimeContent from './TimeContent';
import { useTimeSelection } from './TimeDate';
import { IQuickBookingV2CustomerItem, IQuickBookingV2FormValues, IQuickBookingV2ServiceItem } from './type';
import uuid from 'utils/uuid';
import { ICallerIdCustomer } from 'widgets/CallerId/type';
import { buttonAddCustomerRef } from 'widgets/Customer/AddCustomer';
import { ICustomerItemData } from 'services/shop/types/customer';
import { formatPhone } from 'utils/formatPhone';

type QuickBookingV2Ref = {
  open: (startTime?: string, staffId?: string | null) => void;
  openWithCaller: (customer: ICallerIdCustomer) => void;
};
export const useQuickBookingV2Ref = () => useRef<QuickBookingV2Ref>(null);
export const quickBookingV2Ref = React.createRef<QuickBookingV2Ref>();
const QuickBookingV2 = forwardRef<QuickBookingV2Ref>((_, ref) => {
  const dispatch = useAppDispatch();
  const staffRef = useStaffListChooseUI();
  const staffs = shopSelectors.data.staffs();
  const allSetting = shopSelectors.data.allSetting();
  const staffVerifiedPinCode = appointmentSelectors.staffVerifiedPinCode();
  const [form] = Form.useForm();
  const [visible, setVisible] = useState(false);
  const setLoading = useSetLoadingPage();
  const params = appointmentSelectors.paramsMasterData();
  const timeRef = useTimeSelection();

  const onClose = () => {
    setLoading(false);
    setVisible(false);
  };

  const onOpen: QuickBookingV2Ref['open'] = (startTime, staffId) => {
    const callback = () => {
      form.resetFields();
      let currentDate = moment(params.date, DATE_FORMAT);
      if (startTime) {
        currentDate = moment(startTime);
        form.setFieldValue('defaultStartTime', startTime);
        form.setFieldValue('startTime', currentDate.format('hh:mm A'));
      }
      onPickDate(currentDate);
      form.setFieldValue('defaultStaffId', staffId);
      setVisible(true);
    };

    if (allSetting?.enableVerifyBooking) {
      return verifyPinCode(() => callback(), true);
    }
    callback();
  };

  const onOpenWithCaller: QuickBookingV2Ref['openWithCaller'] = (customer) => {
    const callback = () => {
      form.resetFields();
      const currentDate = moment(params.date, DATE_FORMAT);
      form.setFieldValue('callerId', customer.callerId || '');
      onPickDate(currentDate);
      if (!customer?.id?.toString()) {
        setTimeout(() => {
          buttonAddCustomerRef.current?.open({ phone: formatPhone(customer?.phone || ''), name: customer?.name || '' });
        }, 100);
      } else {
        const newCustomer: ICustomerItemData = {
          id: customer?.id || '',
          name: customer?.name || '',
          phone: customer?.phone || '',
          email: '',
          imageAvatarUrl: '',
          numberVisit: 0,
          point: 0,
        };
        form.setFieldValue('customer', newCustomer);
      }
      setVisible(true);
    };

    if (allSetting?.enableVerifyBooking) {
      return verifyPinCode(() => callback(), true);
    }
    callback();
  };

  const onPickDate = async (date: Moment) => {
    form.setFieldValue('date', date);
    setTimeout(() => {
      if (date.format('MM-DD-YYYY') === moment().format('MM-DD-YYYY')) {
        staffRef.current?.fetchNextAvailable();
      }
      timeRef.current?.fetch(date);
    }, 100);

    form.setFieldValue('loadingTimeSlot', true);
    const defaultStartTime = form.getFieldValue('defaultStartTime');
    try {
      const res: IResponseDataBody<string[]> = await appointmentApis.getAvailableTimeSlotStore(date.format('MM-DD-YYYY'));
      if (res.data.data) {
        const timeSlots = res.data.data.map((o) => moment(o, 'HH:mm').format('hh:mm A'));
        form.setFieldValue('timeSlots', timeSlots);
        if (!defaultStartTime) {
          form.setFieldValue('startTime', timeSlots?.[0] || '');
        }
      }
    } catch (error) {
      form.setFieldValue('timeSlots', []);
    } finally {
      form.setFieldValue('loadingTimeSlot', false);
    }
  };

  const onPickStaff = (item: IStaffItemData) => {
    const activeServiceId: string = form.getFieldValue('activeServiceId');
    const customerList: IQuickBookingV2FormValues['customerList'] = form.getFieldValue('customerList');

    form.setFieldValue('customerList', customerList.map(o => {
      if (activeServiceId?.includes(o.id)) {
        return ({
          ...o,
          services: o.services.map(s => {
            if (s.id === activeServiceId) {
              return ({
                ...s,
                staffId: item.id,
                staffName: item.firstName,
                staffAvatar: item.urlImage,
                anybody: item.anyBody,
              });
            }
            return s;
          })
        });
      }
      return o;
    }));
  };

  const getListStaff = (serviceId: string, servicesProps?: IQuickBookingV2ServiceItem[]) => {
    delay(async () => {
      const values: IQuickBookingV2FormValues = form.getFieldsValue();
      const servicesList = servicesProps || values.services;
      const service = servicesList.find((o) => o.serviceId === serviceId);
      if (!service) return;
      const startTime = service?.masterStartTime
        ? service?.masterStartTime
        : service?.startTime;
      const duration =
        service?.masterDuration !== undefined
          ? service?.masterDuration
          : service?.duration || 0;
      const startTimeMoment = moment(
        values.date.format('MM-DD-YYYY') + ' ' + startTime || '',
        'MM-DD-YYYY hh:mm A'
      );
      const endTimeMoment = startTimeMoment.clone().add(duration, 'minutes');
      const body = {
        serviceIds: [serviceId],
        startTime: startTimeMoment.format('MM-DD-YYYY HH:mm'),
        endTime: endTimeMoment.format('MM-DD-YYYY HH:mm'),
      };
      try {
        staffRef.current?.setLoading(true);
        const res: IResponseDataBody<IStaffItemData[]> =
          await appointmentApis.getListStaffByTime(body);
        if (res.data.data) {
          form.setFieldValue('staffs', res.data.data);
        } else {
          form.setFieldValue('staffs', []);
        }
      } catch (error) {
        form.setFieldValue('staffs', []);
      } finally {
        staffRef.current?.setLoading(false);
      }
    }, 100);
  };

  const onChooseNumCustomer = (value: number) => {
    const customerList: IQuickBookingV2CustomerItem[] = form.getFieldValue('customerList');
    if (!customerList.length) {
      form.setFieldValue('customerList', Array.from({ length: value }, (item, index) => {
        return ({
          id: uuid(),
          name: '',
          customer: null,
          services: [],
        } as IQuickBookingV2CustomerItem);
      }));
      return;
    }

    const cloneCustomerList = [...customerList];

    if (customerList.length > value) {
      form.setFieldValue('customerList', cloneCustomerList.slice(0, value));
      return;
    }

    const length = padEnd(customerList.map(() => 'x').join(''), value, '*').replaceAll('x', '').length;
    Array.from({ length }, (item, index) => {
      const result = ({
        id: uuid(),
        name: '',
        services: [],
      } as IQuickBookingV2CustomerItem);
      cloneCustomerList.push(result);
    });
    form.setFieldValue('customerList', cloneCustomerList);
  };

  const handleFinish = async (values: any) => {
    const formValues = values as IQuickBookingV2FormValues;

    const items: IBodyApiBookingMultiple['items'] = [];
    formValues.customerList.map((c, cIndex) => c.services.map(
      (o) => {
        const startTime =
          moment(formValues.date).format('MM/DD/YYYY') +
          ' ' +
          moment(o.masterStartTime || o.startTime, 'hh:mm A').format('HH:mm');
        const duration = o.masterDuration ? o.masterDuration : o.duration || 0;
        const result: IBodyApiBookingMultipleItem = {
          staffId: o.staffId,
          serviceId: o.serviceId,
          requestStaff: o.aptStatus === 'request',
          startTime,
          endTime: moment(startTime, 'MM/DD/YYYY HH:mm')
            .add(duration, 'minutes')
            .format('MM/DD/YYYY HH:mm'),
          customerName: c.customer?.name || c.name,
          tempCustomerId: c.customer?.id || c.id,
        };
        items.push(result);
      }
    ));

    const body: IBodyApiBookingMultiple = {
      createdBy: staffVerifiedPinCode?.id || '',
      customerId: formValues.customer?.id || '',
      callerId: formValues.callerId || '',
      customerPhone: formValues.customer?.phone || '',
      customerName: formValues.customer?.name || '',
      numberCustomers: formValues.numCustomers || 1,
      note: formValues.note || '',
      repeat: formValues.repeat || 0,
      items,
    };
    setLoading(true);
    try {
      const res: IResponseDataBody<IAppointmentItemData[]> =
        await appointmentApis.bookingMultiple(body);
      if (res.data.data) {
        dispatch(aptUIActions.syncAppointments(res.data.data));
        onClose();
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    open: onOpen,
    openWithCaller: onOpenWithCaller,
  }));

  if (!visible) return null;

  return (
    <Modal
      visible={visible}
      v2
      modalTitle='BOOK BY SERVICES'
      onClose={() => setVisible(false)}
      width={'auto'}
      noneBodyStyle
      noneFooterStyle
      onSubmit={() => form.submit()}
    >
      <FormContainer
        onFinish={handleFinish}
        form={form}
        initialValues={{
          callerId: '',
          date: moment(),
          duration: undefined,
          staffs: undefined,
          numCustomers: 1,
          customerList: [{
            id: uuid(),
            name: '',
            services: [],
          }]
        }}
        onFinishFailed={(errorInfo: any) => {
          const error: string = get(first(errorInfo.errorFields), ['errors', 0], '') || '';
          if (!error) return;
          toast.error(error);
        }}
      >
        <Form.Item noStyle name={'repeat'} />
        <Form.Item noStyle name={'callerId'} />
        <Form.Item noStyle name={'duration'} />
        <QuickBookingCustomerContent />
        <QuickBookingTimeContent timeRef={timeRef} onPickDate={onPickDate} />
        <Box display='flex' flexDirection='column' style={rightContainerStyle}>
          <Box display='flex' flexDirection='row' style={rightContentStyle}>
            <Form.Item name={'services'} noStyle>
              <QuickBookingServiceContent
                staffs={staffs} getListStaff={getListStaff}
                onChooseNumCustomer={onChooseNumCustomer}
              />
            </Form.Item>
            <QuickBookingStaffContent
              onPickStaff={onPickStaff}
              staffRef={staffRef}
            />
          </Box>
        </Box>
      </FormContainer>
    </Modal>
  );
});
QuickBookingV2.displayName = 'QuickBookingV2';
export default QuickBookingV2;

const rightContainerStyle: React.CSSProperties = {
  flex: 2,
  height: '100%',
};
const rightContentStyle: React.CSSProperties = {
  flexGrow: 1,
  background: '#F6F7FC',
};
const FormContainer = styled(Form)`
  display: flex;
  height: 70vh;

  .error-text-validate {
    color: #d2464f;
    text-align: center;
    font-family: Poppins;
    font-size: 18px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
  }
`;


// const onDurationChange = (value?: number | null) => {
//   const formValues: IQuickBookingV2FormValues = form.getFieldsValue();
//   const services =
//     formValues.services?.map((o) => {
//       return {
//         ...o,
//         masterDuration: value,
//       };
//     }) || [];
//   form.setFieldValue('services', services);
// };
